import { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrls } from '../utils/apiUrls';
import { callAPI } from '../utils/apiUtils';
import { toast } from 'react-toastify';
import { resetOrderSession } from '../utils/resetOrderSession';
import useCart from './useCart';
import { isAuthenticated } from '../utils/authUtils';
import {isOfflineAndroid, getOfflineOccupiedTables} from '../utils/androidOfflineCart';


const SKIP_PEOPLE_MODAL_TABS = ["person-tab", "takeaway-tab", "pager-tab"];

/**
 * Everything needed to let staff pick a table and start/resume an order — data fetching,
 * single/multi-select, the people-count modal, checkout/kitchen-print, occupied-timer math.
 * Shared between DashboardPage (grid view) and FloorPlanDashboardPage (visual view) so both
 * screens behave identically; only the rendering differs between them.
 */
export default function useTableOrdering(searchInputRefParam) {
  const navigate = useNavigate();
  const internalSearchInputRef = useRef(null);
  const searchInputRef = searchInputRefParam || internalSearchInputRef;

  const [selectedTables, setSelectedTables] = useState({});
  const [seatingTabs, setSeatingTabs] = useState([]);
  const [tableData, setTableData] = useState({});
  const [activeTab, setActiveTab] = useState('');
  const [search, setSearch] = useState('');
  const [multiSelect, setMultiSelect] = useState({});
  const [occupiedTables, setOccupiedTables] = useState({});
  const [cartModal, setCartModal] = useState({ open: false, tableId: null, tableName: '' });
  const [peopleModal, setPeopleModal] = useState({
    open: false,
    tableId: null,
    tableName: ''
  });
  const [receiptModal, setReceiptModal] = useState({
    open: false,
    tableId: null,
    orderId: null,
    summary: null,
    paymentMethod: null,
  });
  // Only resetCart() is used here — skip the hook's own initial cart fetch (unnecessary here).
  const { resetCart } = useCart(undefined, null, { skipInitialFetch: true });
  const [skipPeopleCountModal, setSkipPeopleCountModal] = useState(false);

  useEffect(() => {
    sessionStorage.removeItem("error_recovered");
  }, []);

  useEffect(() => {
    // Read once on mount - the Settings page is the only place this changes, and it's a
    // low-stakes seating preference, not something that needs to react live if flipped in
    // another tab. Left false (show the prompt, today's behaviour) on any failure.
    (async () => {
      try {
        const res = await callAPI(apiUrls.POS_SETTINGS_GET, {}, 'GET');
        const settings = res?.data?.data || [];
        const setting = settings.find((s) => s.key === 'skip_people_count_modal');
        setSkipPeopleCountModal(!!setting?.value);
      } catch (e) {
        // Default (show the prompt) is always the safe fallback here.
      }
    })();
  }, []);

  useEffect(() => {
    const raw = localStorage.getItem('selectedTables');
    if (raw) {
      try {
        setSelectedTables(JSON.parse(raw));
      } catch {
        setSelectedTables({});
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    localStorage.removeItem('selectedTables');
    resetOrderSession();
    resetCart();

    setSelectedTables({});
  }, []);

  useEffect(() => {
    async function fetchData() {
      console.log("========== TABLE_DATA START ==========");

      try {
        console.log("[TABLE_DATA] Calling API:", apiUrls.TABLE_DATA);
        const res = await callAPI(apiUrls.TABLE_DATA, {}, 'GET');

        console.log("[TABLE_DATA] Response received:", res);
        console.log("[TABLE_DATA] Response data:", res?.data);
        console.log("[TABLE_DATA] Response data.data:", res?.data?.data);

        const seatingTabs = res.data?.data?.seatingTabs || {};
        const tableData = res.data?.data?.tableData || {};

        console.log(
          "[TABLE_DATA] seatingTabs:",
          seatingTabs
        );

        console.log(
          "[TABLE_DATA] tableData:",
          tableData
        );

        console.log(
          "[TABLE_DATA] seatingTabs keys:",
          Object.keys(seatingTabs)
        );

        console.log(
          "[TABLE_DATA] tableData keys:",
          Object.keys(tableData)
        );

        Object.entries(tableData).forEach(([key, tables]) => {
        console.log(
          `[TABLE_DATA] tableData[${key}] count:`,
          Array.isArray(tables) ? tables.length : 'NOT_ARRAY'
        );

        console.log(
          `[TABLE_DATA] tableData[${key}] value:`,
          tables
        );
      });

        const DEFAULT_TAB = {
          id: "default-tab",
          label: "Default",
          icon: "./images/sprite.svg#iconTab"
        };

        let cleanedTabs = [];
        let cleanedTableData = { "default-tab": [] };

        Object.entries(seatingTabs).forEach(([key, tab]) => {
          const tabId = tab?.id;

         console.log(
          "[TABLE_DATA] Processing seating tab:",
          {
            key,
            tab,
            tabId,
            matchingTableData: tableData[tabId]
          }
        );

          if (!tabId) {
            if (tableData[key]) cleanedTableData["default-tab"].push(...tableData[key]);
            return;
          }
          if (tableData[tabId] && tableData[tabId].length > 0) {
            cleanedTabs.push(tab);
            cleanedTableData[tabId] = tableData[tabId];
          }
        });

        if (cleanedTableData["default-tab"].length > 0) {
          cleanedTabs.push(DEFAULT_TAB);
        }

       console.log(
        "[TABLE_DATA] cleanedTabs:",
        cleanedTabs
      );

      console.log(
        "[TABLE_DATA] cleanedTableData:",
        cleanedTableData
      );


        setSeatingTabs(cleanedTabs);
        setTableData(cleanedTableData);
        if (cleanedTabs.length > 0) setActiveTab(cleanedTabs[0].id);

        const multiInit = {};
        cleanedTabs.forEach(tab => multiInit[tab.id] = false);
        setMultiSelect(multiInit);
        console.log("========== TABLE_DATA SUCCESS ==========");
      } catch (err) {

        console.error("========== TABLE_DATA FAILED ==========");

        console.error("[TABLE_DATA] Error object:", err);
        console.error("[TABLE_DATA] Error message:", err?.message);
        console.error("[TABLE_DATA] Error code:", err?.code);
        console.error("[TABLE_DATA] Error name:", err?.name);
        console.error("[TABLE_DATA] Error response:", err?.response);
        console.error("[TABLE_DATA] Error response data:", err?.response?.data);
        console.error("[TABLE_DATA] Error request:", err?.request);

        console.error(
          "[TABLE_DATA] Full error JSON:",
          JSON.stringify(err, Object.getOwnPropertyNames(err), 2)
        );

        console.error("========== TABLE_DATA FAILED ==========");
      }
    }
    fetchData();
  }, []);

  const logSelectedTablesStorage = (label) => {
  console.log(
    `[DINEIN_TABLE] ${label}:`,
    JSON.stringify({
      isOffline: navigator.onLine === false,
      localStorageValue: localStorage.getItem('selectedTables')
    })
  );
};


  // Named at hook scope (not nested in the effect below) so handlePagerTap can also call it
  // directly after releasing a pager, to refresh its badge without a full page reload.
  const fetchOccupied = async () => {
    try {
       let occupiedData = [];

      if (isOfflineAndroid()) {

        console.log(
          '[OCCUPIED_TABLES] OFFLINE - reading from Room'
        );

        const response =
          await getOfflineOccupiedTables();

        console.log(
          '[OCCUPIED_TABLES] Offline response:',
          response
        );

        occupiedData =
          response?.data || [];

      } else {
           console.log(
          '[OCCUPIED_TABLES] ONLINE - calling API'
        );

          const res = await callAPI(apiUrls.OCCUPIED_TABLE_WITH_ORDERS, {}, 'GET', null, {}, {}, false);
          occupiedData = res?.data?.data || [];
      }


      const map = {};
      occupiedData.forEach(t => {
         if (!t?.id) {
          return;
        }
        map[String(t.id)]  = {
          started: t.ordered_started_at,
          amount: parseFloat(t.order_amount || 0).toFixed(2),
          customerName: t.customer?.name || null,
          orderName: t.customer?.order_name?.trim() || null,
          localOrderId: t.localOrderId || null,
          backendOrderId: t.backendOrderId || null,
          status: t.status || null,
          orderType: t.orderType || null
        };
      });
        console.log(
          '[OCCUPIED_TABLES] Occupied map:',
          map
        );
      setOccupiedTables(map);
    } catch (err) {
      console.error('Failed to fetch occupied tables', err);
    }
  };

  useEffect(() => {
    fetchOccupied();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setSearch('');
  };

  const handleSearch = (e) => setSearch(e.target.value);

  const handleMultiSelect = (tabId) => {
    setMultiSelect(prev => ({ ...prev, [tabId]: !prev[tabId] }));
    setSelectedTables(prev => {
      const updated = { ...prev, [tabId]: [] };
      localStorage.setItem('selectedTables', JSON.stringify(updated));
      return updated;
    });
  };

  const handleTableSelect = (tabId, tableId, tableName) => {
    const isOccupied = !!occupiedTables[tableId];

    if (multiSelect[tabId]) {
      if (isOccupied) {
        toast.warning(
          "Occupied tables cannot be selected in multiple selection."
        );
        return;
      }

      setSelectedTables(prev => {
        const current = prev[tabId] || [];

        const isAlreadySelected = current.some(
          id => String(id) === String(tableId)
        );

        let updatedTables;

        if (isAlreadySelected) {
          updatedTables = current.filter(
            id => String(id) !== String(tableId)
          );
        } else {
          updatedTables = [...current, tableId];
        }

        const finalState = {
          ...prev,
          [tabId]: updatedTables
        };

        localStorage.setItem(
          'selectedTables',
          JSON.stringify(finalState)
        );
        logSelectedTablesStorage('AFTER TABLE SELECT');
        return finalState;
      });

      return;
    }

    const currentSelected = selectedTables?.[tabId] || [];

    const isAlreadySelected = currentSelected.some(
      id => String(id) === String(tableId)
    );

    if (isAlreadySelected) {
      const updated = {
        ...selectedTables,
        [tabId]: []
      };

      setSelectedTables(updated);

      localStorage.setItem(
        'selectedTables',
        JSON.stringify(updated)
      );
      logSelectedTablesStorage('AFTER SINGLE TABLE SELECT 1');
      return;
    }

    const updated = {
      [tabId]: [tableId]
    };

    setSelectedTables(updated);

    localStorage.setItem(
      'selectedTables',
      JSON.stringify(updated)
    );
    logSelectedTablesStorage('AFTER SINGLE TABLE SELECT 2');
    setMultiSelect(prev => {
      const reset = {};

      Object.keys(prev).forEach(id => {
        reset[id] = false;
      });

      return reset;
    });

    localStorage.setItem('selectedTableName', tableName || '');
    localStorage.setItem('selectedTabId', tabId || '');

    if (isOccupied) {
      navigate('/takeaway', {
        state: {
          orderType: 'dinein',
          selectedTables: [tableId],
          tableName,
          tabId
        }
      });

      return;
    }

    if (!SKIP_PEOPLE_MODAL_TABS.includes(tabId) && !skipPeopleCountModal) {
      setPeopleModal({
        open: true,
        tabId,
        tableId,
        tableName,
      });

      return;
    }

    navigate('/takeaway', {
      state: {
        orderType: 'dinein',
        selectedTables: [tableId],
        tableName,
        tabId
      }
    });
  };

  /**
   * Pagers are the one table type where tapping an occupied tile doesn't navigate into its
   * order - a single tap just releases it (the physical device is back at the counter),
   * independent of whether the order itself is done. Tapping an available pager is identical to
   * any other table (SKIP_PEOPLE_MODAL_TABS already covers pager-tab above).
   */
  const handlePagerTap = async (tableId, tableName) => {
    if (occupiedTables[tableId]) {
      try {
        await callAPI(apiUrls.RELEASE_PAGER_OCCUPANCY, {}, 'POST', { table_id: tableId });
        toast.success('Pager released');
        fetchOccupied();
      } catch (err) {
        console.error('Failed to release pager', err);
        toast.error('Failed to release pager');
      }
      return;
    }

    handleTableSelect('pager-tab', tableId, tableName);
  };

  const handlePeopleModalSuccess = (numPeople) => {
    const { tabId, tableId } = peopleModal;

    setTableData(prev => {
      const updatedTables = prev[tabId].map(t =>
        t.id === tableId ? { ...t, personNumber: numPeople } : t
      );
      return { ...prev, [tabId]: updatedTables };
    });

    setPeopleModal({ open: false, tabId: null, tableId: null, tableName: '' });
  };

  const handlePeopleModalNext = () => {
    const { tabId, tableId, tableName } = peopleModal;

    const updated = {
      [tabId]: [tableId]
    };

    setSelectedTables(updated);

    localStorage.setItem(
      'selectedTables',
      JSON.stringify(updated)
    );
    localStorage.setItem('selectedTableName', tableName || '');
    localStorage.setItem('selectedTabId', tabId || '');

    navigate('/takeaway', {
      state: {
        orderType: 'dinein',
        selectedTables: [tableId],
        tableName,
        tabId
      }
    });
  };

  const handleInfoClick = (tableId, tableName) => {
    if (occupiedTables[tableId]) {
      setCartModal({ open: true, tableId, tableName });
    } else {
      alert(`Table ${tableName} is not occupied yet.`);
    }
  };

  /**
   * The small round receipt icon on an occupied pager (PagerTile.js) - lets staff glance at
   * what's on the order before handing the pager over, without leaving the dashboard. Reuses
   * DigitalReceiptModal (the same "printed" receipt shown right after checkout) but backs it with
   * a dedicated read-only summary endpoint instead of the live checkout-summary recalculation
   * one, since this order is already paid - see actionReceiptSummary()'s own comment for why.
   */
  const handleViewReceipt = async (tableId) => {
    try {
      const res = await callAPI(`${apiUrls.RECEIPT_SUMMARY}?table_id=${tableId}`, {}, 'GET');
      const data = res?.data?.data;
      if (!data) {
        toast.error('Could not load receipt');
        return;
      }
      setReceiptModal({
        open: true,
        tableId,
        orderId: data.order_id,
        summary: { gross_total: data.gross_total, net_total: data.net_total, table: data.table },
        paymentMethod: data.payment_method,
        // Only PagerTile calls this handler today, so the row this backs (DigitalReceiptModal's
        // tableLabel prop) is always "Pager" here rather than "Table".
        tableLabel: 'Pager',
        serverName: data.server_name,
      });
    } catch (err) {
      console.error('Failed to load receipt summary', err);
      toast.error('Failed to load receipt');
    }
  };

  const closeReceiptModal = () => {
    setReceiptModal({ open: false, tableId: null, orderId: null, summary: null, paymentMethod: null });
  };

  const printKitchenDocket = async (tableId, type) => {
    const formData = new FormData();
    formData.append('table_id', tableId);
    formData.append('type', type);
    try {
      const res = await callAPI(apiUrls.PRINT_KITCHEN_DOCKET, {}, 'POST', formData, {});
      toast.success(res.data.message || 'Print sent successfully!');
    } catch {
      toast.error('Print did not send!');
    }
  };

  const getElapsed = (startTime) => {
    if (!startTime) return '';
    const start = new Date(startTime);
    const sec = Math.floor((new Date() - start) / 1000);
    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  };

  const filteredTableData = useMemo(() => {
    if (!search.trim()) return tableData;

    const query = search.toLowerCase().trim();
    const filtered = {};

    seatingTabs.forEach(tab => {
      const tables = tableData[tab.id] || [];
      filtered[tab.id] = tables.filter(t => {
        const tableName = (t.name || `${t.tableNumber}`).toLowerCase();
        return (
          tableName.includes(query) ||
          t.tableNumber?.toString().includes(query) ||
          t.personNumber?.toString().includes(query)
        );
      });
    });

    return filtered;
  }, [search, tableData, seatingTabs]);

  useEffect(() => {
    if (!search.trim()) return;

    const firstTabWithResults = seatingTabs.find(
      tab => (filteredTableData[tab.id] || []).length > 0
    );

    if (firstTabWithResults) {
      setActiveTab(firstTabWithResults.id);
      setTimeout(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.setSelectionRange(search.length, search.length);
      }, 0);
    }
  }, [search, seatingTabs, filteredTableData]);

  const handleCheckoutClick = (tabId, tableId) => {
    const updated = { [tabId]: [tableId] };
    setSelectedTables(updated);

    localStorage.setItem('selectedTables', JSON.stringify(updated));

    navigate(`/payment-view?table_id=${tableId}`);
  };

  const anySelected = Object.values(selectedTables || {}).some(arr => arr.length > 0);

  const handleNext = () => {
    if (!anySelected) {
      alert('Select table first');
      return;
    }

    const flatTableIds = Object.values(selectedTables)
      .flat()
      .filter(Boolean);

    const allTables = Object.values(tableData).flat();
    const tableName = flatTableIds
      .map(id => {
        const table = allTables.find(t => String(t.id) === String(id));
        return table ? (table.name || `${table.tableNumber}`) : null;
      })
      .filter(Boolean)
      .join(', ');

    // Multi-select only ever operates within one tab at a time, so the first tab with a
    // selection is the order's tab - same signal handleTableSelect passes for a single tap.
    const tabId = Object.keys(selectedTables).find(id => (selectedTables[id] || []).length > 0);

    localStorage.setItem('selectedTableName', tableName || '');
    localStorage.setItem('selectedTabId', tabId || '');

    console.log(
      '[DINEIN_TABLE] HANDLE NEXT:',
      JSON.stringify({
        isOffline: navigator.onLine === false,
        selectedTables,
        flatTableIds,
        localStorageValue: localStorage.getItem('selectedTables')
      })
    );


    navigate('/takeaway', {
      state: {
        orderType: 'dinein',
        selectedTables: flatTableIds,
        tableName,
        tabId
      }
    });
  };

  return {
    searchInputRef,
    selectedTables,
    seatingTabs,
    tableData,
    activeTab,
    setActiveTab,
    search,
    setSearch,
    multiSelect,
    occupiedTables,
    cartModal,
    setCartModal,
    peopleModal,
    setPeopleModal,
    receiptModal,
    closeReceiptModal,
    skipPeopleModalTabs: SKIP_PEOPLE_MODAL_TABS,
    handleTabClick,
    handleSearch,
    handleMultiSelect,
    handleTableSelect,
    handlePagerTap,
    handlePeopleModalSuccess,
    handlePeopleModalNext,
    handleInfoClick,
    handleViewReceipt,
    printKitchenDocket,
    getElapsed,
    filteredTableData,
    handleCheckoutClick,
    anySelected,
    handleNext,
  };
}
